import React from 'react';
import { Form, Select, Tooltip, Typography } from 'antd';
import { FormInstance } from 'antd/es/form';
// Hooks
import { useGetAudienceFilters } from 'hooks';
// Components
import LocationSelect from 'components/common/LocationSelect/LocationSelect';
// Styles
import styles from './CustomCampaignAudienceFilters.module.scss';

const { Text } = Typography;
const { Option } = Select;

type CustomCampaignAudienceFiltersProps = {
  form: FormInstance<any>;
  canEditPremiumFilters: boolean;
};

const CustomCampaignAudienceFilters = ({
  form,
  canEditPremiumFilters,
}: CustomCampaignAudienceFiltersProps): JSX.Element => {
  const { data: audienceFiltersData } = useGetAudienceFilters();
  const { ageRanges, ethnicities, genders, interests } =
    audienceFiltersData?.getAudienceFilters || {};
  const tooltipTitle = canEditPremiumFilters
    ? undefined
    : 'Brand is on free subscription';

  return (
    <div className={styles.filterGroup}>
      <h4 className={styles.sectionTitle}>
        Audience Filters{' '}
        {!canEditPremiumFilters && '- Brand is on free subscription'}
      </h4>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Optional filters to refine talent by audience demographics.
      </Text>

      <Tooltip title={tooltipTitle}>
        <div className={styles.row}>
          <Form.Item name="audienceGender" label={<Text>Audience Gender</Text>}>
            <Select
              placeholder="Select gender"
              allowClear
              disabled={!canEditPremiumFilters}
            >
              {genders?.map((item) => (
                <Option value={item.value} key={item.value}>
                  {item.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="audienceAgeRanges" label={<Text>Audience Age</Text>}>
            <Select
              mode="multiple"
              placeholder="Select age ranges"
              allowClear
              disabled={!canEditPremiumFilters}
            >
              {ageRanges?.map((item) => (
                <Option value={item.value} key={item.value}>
                  {item.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="audienceEthnicities"
            label={<Text>Audience Ethnicity</Text>}
          >
            <Select
              mode="multiple"
              placeholder="Select ethnicities"
              allowClear
              disabled={!canEditPremiumFilters}
            >
              {ethnicities?.map((item) => (
                <Option value={item.value} key={item.value}>
                  {item.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>
      </Tooltip>

      <Tooltip title={tooltipTitle}>
        <div className={`${styles.row} ${styles.twoCol}`}>
          <Form.Item
            name="audienceInterests"
            label={<Text>Audience Interests</Text>}
          >
            <Select
              mode="multiple"
              placeholder="Select interests"
              allowClear
              disabled={!canEditPremiumFilters}
            >
              {interests?.map((item) => (
                <Option value={item.value} key={item.value}>
                  {item.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item shouldUpdate noStyle>
            {() => (
              <Form.Item label={<Text>Audience Location</Text>}>
                <LocationSelect
                  id="audienceLocations"
                  value={form.getFieldValue('audienceLocations') || []}
                  onChange={(audienceLocations) =>
                    form.setFieldsValue({ audienceLocations })
                  }
                  placeholder="Search country, state, or city"
                  disabled={!canEditPremiumFilters}
                />
              </Form.Item>
            )}
          </Form.Item>
        </div>
      </Tooltip>
    </div>
  );
};

export default CustomCampaignAudienceFilters;
